import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileSpreadsheet, Printer, RefreshCw, AlertTriangle, Loader2, ArrowUpDown } from 'lucide-react';
import api from '../../utils/api';
import { downloadExcel } from '../../utils/excel';
import { printTable } from '../../utils/printTable';
import { fmt4 } from '../../utils/cft';
import { useSettings } from '../../context/SettingsContext';

// CFT page → Detailed CFT Report: wood stock ledger — Opening, Inward (+ Returns), Outward, Closing.
export const KIND = {
  opening: { label: 'Opening Balance', cls: 'bg-blue-100 text-blue-700' },
  inward: { label: 'Inward', cls: 'bg-green-100 text-green-700' },
  return: { label: 'Return', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  outward: { label: 'Outward (Issue)', cls: 'bg-orange-100 text-orange-700' },
  transfer_in: { label: 'Transfer In', cls: 'bg-purple-50 text-purple-700 border border-purple-200' },
  transfer_out: { label: 'Transfer Out', cls: 'bg-purple-100 text-purple-800' },
};
const fmtDate = d => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const qtyFmt = n => (n ? String(Math.round(n * 100) / 100) : '');

// Wood names for the filters: the master plus wood names typed on older CFT sheets
export function useWoodNames() {
  const [names, setNames] = useState([]);
  useEffect(() => {
    api.get('/wood-types').then(r => setNames([...(r.data.rows || []).map(w => w.name), ...(r.data.legacy_names || [])].sort((a, b) => a.localeCompare(b))))
      .catch(() => {});
  }, []);
  return names;
}

export function DimFilters({ f, set }) {
  return (
    <>
      <label className="text-xs text-gray-500">Label<input value={f.label} onChange={e => set({ label: e.target.value })} placeholder="e.g. Plank" className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-800" /></label>
      {[['l', 'L (mm)'], ['w', 'W (mm)'], ['t', 'T (mm)']].map(([k, l]) => (
        <label key={k} className="text-xs text-gray-500">{l}<input type="number" min="0" value={f[k]} onChange={e => set({ [k]: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-800" /></label>
      ))}
    </>
  );
}

const PAGE = 50;
export default function WoodStockReport() {
  const navigate = useNavigate();
  const base = useLocation().pathname.startsWith('/worker') ? '/worker/cft' : '/admin/cft';
  const { settings } = useSettings() || {};
  const woods = useWoodNames();
  const [f, setF] = useState({ wood: '', from: '', to: '', type: '', label: '', l: '', w: '', t: '', ref: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ key: 'seq', dir: 1 });
  const set = p => { setF(x => ({ ...x, ...p })); setPage(0); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
      const r = await api.get('/wood-stock/ledger', { params });
      setData({ ...r.data, rows: r.data.rows.map((x, i) => ({ ...x, seq: i })) });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const s = data?.summary || {};
  const sorted = useMemo(() => {
    const list = [...(data?.rows || [])];
    const { key, dir } = sort;
    list.sort((a, b) => {
      const x = a[key], y = b[key];
      if (typeof x === 'number' || typeof y === 'number') return ((x || 0) - (y || 0)) * dir;
      return String(x || '').localeCompare(String(y || '')) * dir || a.seq - b.seq;
    });
    return list;
  }, [data, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const view = sorted.slice(page * PAGE, page * PAGE + PAGE);
  const sortBy = key => setSort(x => ({ key, dir: x.key === key ? -x.dir : 1 }));

  const filterText = () => [f.wood && `Wood: ${f.wood}`, (f.from || f.to) && `Period: ${f.from || 'start'} → ${f.to || 'today'}`,
    f.type && `Type: ${KIND[f.type]?.label}`, f.label && `Label: ${f.label}`, f.l && `L ${f.l}`, f.w && `W ${f.w}`, f.t && `T ${f.t}`, f.ref && `Ref: ${f.ref}`]
    .filter(Boolean).join(' · ') || 'All wood, all dates';
  const summaryList = [
    ['Opening Stock (CFT)', fmt4(s.opening_cft)], ['Opening Balance entries', fmt4(s.opening_entries_cft)],
    ['Inward (CFT)', fmt4(s.inward_cft)], ['Return (CFT)', fmt4(s.return_cft)], ['Outward (CFT)', fmt4(s.outward_cft)], ['Closing Stock (CFT)', fmt4(s.closing_cft)],
  ];
  const cols = [
    { label: 'Date', value: r => fmtDate(r.date) }, { label: 'Transaction Type', value: r => KIND[r.kind]?.label },
    { label: 'Reference No.', value: r => r.ref_no }, { label: 'Wood Name', value: r => r.wood_name }, { label: 'Label', value: r => r.label || '' },
    { label: 'L (mm)', value: r => r.length, align: 'r' }, { label: 'W (mm)', value: r => r.width, align: 'r' }, { label: 'T (mm)', value: r => r.thickness, align: 'r' },
    { label: 'Inward Qty', value: r => qtyFmt(r.in_qty), align: 'r' }, { label: 'Outward Qty', value: r => qtyFmt(r.out_qty), align: 'r' },
    { label: 'Inward CFT', value: r => (r.in_cft ? fmt4(r.in_cft) : ''), align: 'r' }, { label: 'Outward CFT', value: r => (r.out_cft ? fmt4(r.out_cft) : ''), align: 'r' },
    { label: 'Closing CFT', value: r => fmt4(r.closing_cft), align: 'r' },
  ];
  const exportExcel = async () => {
    try {
      await downloadExcel(`Detailed_CFT_Report_${new Date().toISOString().slice(0, 10)}.xlsx`, [
        { name: 'Summary', rows: [['Detailed CFT Report'], ['Filters', filterText()], [], ...summaryList] },
        { name: 'Stock Ledger', rows: [cols.map(c => c.label), ...sorted.map(r => cols.map(c => {
          const v = c.value(r); return c.align === 'r' && v !== '' ? Number(v) : v;
        }))] },
        ...(data.negative?.length ? [{ name: 'Negative Stock', rows: data.negative.map(n => ({ Wood: n.wood_name, Label: n.label || '', 'L (mm)': n.length, 'W (mm)': n.width, 'T (mm)': n.thickness, 'Stock Qty': n.qty, 'Stock CFT': n.cft })) }] : []),
      ]);
    } catch (err) { toast.error(err.message || 'Export failed'); }
  };
  const print = () => printTable({ title: 'Detailed CFT Report', subtitle: filterText(), company: settings?.company_name, summary: summaryList, columns: cols, rows: sorted,
    rowClass: r => (r.negative ? 'neg' : r.kind === 'return' ? 'ret' : r.kind === 'opening' ? 'open' : '') }) || toast.error('Allow pop-ups to print');

  const Card = ({ label, value, cls, sub }) => (
    <div className={`rounded-xl border p-3 ${cls}`}><div className="text-lg font-bold">{fmt4(value)}</div><div className="text-[11px] font-medium opacity-80">{label}</div>{sub && <div className="text-[10px] opacity-70">{sub}</div>}</div>
  );
  const Th = ({ k, children, right }) => (
    <th className={`px-2.5 py-2.5 ${right ? 'text-right' : ''}`}><button onClick={() => sortBy(k)} className={`inline-flex items-center gap-1 uppercase ${sort.key === k ? 'text-blue-700' : ''}`}>{children}<ArrowUpDown className="w-3 h-3 opacity-50" /></button></th>
  );

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-gray-200 p-3 grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2">
        <label className="text-xs text-gray-500 col-span-2">Wood Name
          <select value={f.wood} onChange={e => set({ wood: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-800 font-medium">
            <option value="">All woods</option>{woods.map(n => <option key={n} value={n}>{n}</option>)}
          </select></label>
        <label className="text-xs text-gray-500">From<input type="date" value={f.from} onChange={e => set({ from: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">To<input type="date" value={f.to} onChange={e => set({ to: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">Type
          <select value={f.type} onChange={e => set({ type: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-800">
            <option value="">All</option><option value="inward">Inward</option><option value="outward">Outward</option><option value="return">Return</option><option value="opening">Opening Balance</option><option value="transfer_in">Transfer In</option><option value="transfer_out">Transfer Out</option>
          </select></label>
        <DimFilters f={f} set={set} />
        <label className="text-xs text-gray-500">CFT Sheet / Ref.<input value={f.ref} onChange={e => set({ ref: e.target.value })} placeholder="CFT-…, WIN-…" className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-800" /></label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card label="Opening Stock" value={s.opening_cft} cls="bg-gray-50 border-gray-200 text-gray-800" sub={f.from ? `before ${fmtDate(f.from)}` : 'before first entry'} />
        <Card label="Opening Balance entries" value={s.opening_entries_cft} cls="bg-blue-50 border-blue-200 text-blue-800" />
        <Card label="Inward" value={s.inward_cft} cls="bg-green-50 border-green-200 text-green-800" sub={`${s.inward_qty || 0} pcs`} />
        <Card label="Return (counted as inward)" value={s.return_cft} cls="bg-emerald-50 border-emerald-200 text-emerald-800" sub={`${s.return_qty || 0} pcs`} />
        <Card label="Outward (Issued)" value={s.outward_cft} cls="bg-orange-50 border-orange-200 text-orange-800" sub={`${s.outward_qty || 0} pcs`} />
        <Card label="Closing Stock" value={s.closing_cft} cls={`${Number(s.closing_cft) < 0 ? 'bg-red-50 border-red-300 text-red-700' : 'bg-purple-50 border-purple-200 text-purple-800'}`} sub="Opening + Inward + Return − Outward" />
      </div>

      {data?.negative?.length > 0 && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div><strong>Negative stock</strong> — more was issued than was received for {data.negative.length} size(s). Check the inward entries or record an opening balance:
            <div className="mt-1 flex flex-wrap gap-1.5">{data.negative.slice(0, 12).map((n, i) => (
              <span key={i} className="px-2 py-0.5 rounded bg-white border border-red-200 text-xs">{n.wood_name} · {n.label || '—'} {n.length}×{n.width}×{n.thickness}: {n.qty} pcs / {fmt4(n.cft)} CFT</span>
            ))}</div></div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-gray-500">{data ? `${data.shown} transaction(s)` : ''}{loading && data ? ' · updating…' : ''} · Closing CFT is the running balance in date order</span>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>
          <button onClick={print} disabled={!data} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={exportExcel} disabled={!data} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"><FileSpreadsheet className="w-4 h-4" /> Export Excel</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {!data ? <div className="p-10 text-center text-sm text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : !sorted.length ? <div className="p-10 text-center text-sm text-gray-400">No stock movement for these filters</div>
          : (
            <table className="w-full text-sm min-w-[1250px]">
              <thead><tr className="bg-gray-50 text-left text-[11px] text-gray-500">
                <Th k="date">Date</Th><Th k="kind">Type</Th><Th k="ref_no">Reference No.</Th><Th k="wood_name">Wood Name</Th><Th k="label">Label</Th>
                <Th k="length" right>L</Th><Th k="width" right>W</Th><Th k="thickness" right>T</Th>
                <Th k="in_qty" right>Inward Qty</Th><Th k="out_qty" right>Outward Qty</Th><Th k="in_cft" right>Inward CFT</Th><Th k="out_cft" right>Outward CFT</Th><Th k="seq" right>Closing CFT</Th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {page === 0 && sort.key === 'seq' && sort.dir === 1 && (
                  <tr className="bg-gray-50/80 text-xs font-semibold text-gray-600"><td className="px-2.5 py-2" colSpan={12}>Opening Stock {f.from ? `(before ${fmtDate(f.from)})` : ''}</td><td className="px-2.5 py-2 text-right">{fmt4(s.opening_cft)}</td></tr>
                )}
                {view.map(r => (
                  <tr key={r.key} className={r.negative ? 'bg-red-50' : 'hover:bg-gray-50'}>
                    <td className="px-2.5 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-2.5 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${KIND[r.kind]?.cls}`}>{KIND[r.kind]?.label}</span></td>
                    <td className="px-2.5 py-2">{r.src === 'cft'
                      ? <button onClick={() => navigate(`${base}/${r.doc_id}`)} className="text-blue-700 hover:underline whitespace-nowrap">{r.ref_no}</button>
                      : <span className="whitespace-nowrap">{r.ref_no}</span>}
                      {(r.project_name || r.note) && <div className="text-[11px] text-gray-400 truncate max-w-[12rem]">{r.project_name || r.note}</div>}</td>
                    <td className="px-2.5 py-2 font-medium text-gray-800">{r.wood_name}</td>
                    <td className="px-2.5 py-2 text-gray-600">{r.label || '—'}</td>
                    <td className="px-2.5 py-2 text-right">{r.length}</td><td className="px-2.5 py-2 text-right">{r.width}</td><td className="px-2.5 py-2 text-right">{r.thickness}</td>
                    <td className="px-2.5 py-2 text-right text-green-700">{qtyFmt(r.in_qty)}</td><td className="px-2.5 py-2 text-right text-orange-700">{qtyFmt(r.out_qty)}</td>
                    <td className="px-2.5 py-2 text-right text-green-700">{r.in_cft ? fmt4(r.in_cft) : ''}</td><td className="px-2.5 py-2 text-right text-orange-700">{r.out_cft ? fmt4(r.out_cft) : ''}</td>
                    <td className={`px-2.5 py-2 text-right font-bold ${r.negative ? 'text-red-600' : 'text-gray-800'}`}>{fmt4(r.closing_cft)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="bg-gray-50 font-bold text-sm">
                <td className="px-2.5 py-2" colSpan={8}>Totals ({sorted.length} shown)</td>
                <td className="px-2.5 py-2 text-right">{qtyFmt(sorted.reduce((a, r) => a + r.in_qty, 0))}</td><td className="px-2.5 py-2 text-right">{qtyFmt(sorted.reduce((a, r) => a + r.out_qty, 0))}</td>
                <td className="px-2.5 py-2 text-right">{fmt4(sorted.reduce((a, r) => a + r.in_cft, 0))}</td><td className="px-2.5 py-2 text-right">{fmt4(sorted.reduce((a, r) => a + r.out_cft, 0))}</td>
                <td className="px-2.5 py-2 text-right">{fmt4(s.closing_cft)}</td>
              </tr></tfoot>
            </table>
          )}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Previous</button>
          <span className="text-gray-500">Page {page + 1} of {pages}</span>
          <button disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
